import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Calendar, CalendarDays, CheckCircle2, Lock, Map, Pencil, XCircle } from "lucide-react";
import { inr, fmtDate } from "@/lib/format";
import { GovBadge } from "@/components/GovBadge";
import { ErrorState, LoadingState } from "@/components/States";
import { api, formatApiError } from "@/lib/api";

function KPI({ icon: Icon, label, value, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="group rounded-lg border border-[#E2DFD7] bg-white p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[#9FC4B4] hover:shadow-md">
      <span className="mb-2 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#EEF6F2] transition-colors group-hover:bg-[#E1EFE8]">
        <Icon className="h-4 w-4 text-[#2D7A65]" />
      </span>
      <p className="text-2xl font-bold text-[#1A2420]">{value}</p>
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
    </button>
  );
}

const STATUS_ICON = {
  ok: CheckCircle2,
  warn: AlertTriangle,
  blocker: XCircle,
  restricted: Lock,
};
const STATUS_CLASS = {
  ok: "text-emerald-700",
  warn: "text-amber-700",
  blocker: "text-rose-700",
  restricted: "text-slate-500",
};

const HIDDEN_OVERVIEW_READINESS_KEYS = new Set(["documents", "tasks", "briefing", "risk", "participants", "payments"]);

function clampPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

const BATCH_STATUS_LABELS = {
  open: "Open",
  filling_fast: "Filling fast",
  sold_out: "Sold out",
  closed: "Closed",
};
const BATCH_STATUS_CLASS = {
  open: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  filling_fast: "bg-amber-50 text-amber-700 ring-amber-200",
  sold_out: "bg-rose-50 text-rose-700 ring-rose-200",
  closed: "bg-slate-100 text-slate-600 ring-slate-200",
};
function BatchFacts({ batch, index }) {
  return (
    <section className="group relative overflow-hidden rounded-2xl border border-[#D9E1DC] bg-[#FFFCF7] p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[#BFD5C6] hover:shadow-md" data-testid={`overview-batch-facts-${batch.id || index}`}>
      <span className={`absolute inset-x-0 top-0 h-1 ${index % 2 === 0 ? "bg-[#2D7A65]" : "bg-[#E5B85C]"}`} aria-hidden="true" />
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#EAF4EF] text-[11px] font-black text-[#2D7A65]">{String(index + 1).padStart(2, "0")}</span>
          <div className="min-w-0">
            <p className="text-[9px] font-black uppercase tracking-[0.12em] text-slate-400">Departure</p>
            <h4 className="truncate text-sm font-black text-[#1A2420]">{batch.label || `Batch ${index + 1}`}</h4>
          </div>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ${BATCH_STATUS_CLASS[batch.status] || BATCH_STATUS_CLASS.open}`}>
          {BATCH_STATUS_LABELS[batch.status] || batch.status || "Open"}
        </span>
      </div>
      <div className="mt-4 flex items-end justify-between gap-3 border-b border-[#E5E9E6] pb-3">
        <div>
          <p className="text-[9px] font-black uppercase tracking-[0.1em] text-slate-400">Trip price</p>
          <p className="mt-0.5 text-xl font-black text-[#17211D]">{inr(batch.cost_minor)}</p>
        </div>
        <p className="pb-0.5 text-xs font-bold text-[#607069]">{Number(batch.capacity) > 0 ? `${batch.capacity} seats` : "Capacity not set"}</p>
      </div>
      <div className="mt-3 flex items-start gap-2">
        <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-[#2D7A65]" />
        <div className="min-w-0">
          <p className="truncate text-xs font-bold text-[#1A2420]">{fmtDate(batch.start_date)} – {fmtDate(batch.end_date)}</p>
          <p className="mt-0.5 text-[10px] font-medium text-slate-500">{batch.duration_days || "—"} days · {batch.deposit_pct ?? 25}% deposit</p>
        </div>
      </div>
    </section>
  );
}

function ReadinessPanel({ trip, onGoTab }) {
  const [readiness, setReadiness] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get(`/trips/${trip.id}/readiness`);
      setReadiness(data);
    } catch (err) {
      setError(formatApiError(err, "Could not load trip readiness."));
    }
  }, [trip.id]);

  useEffect(() => { load(); }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!readiness) return <LoadingState label="Loading readiness..." />;

  const visibleReadinessItems = (readiness.items || readiness.checklist || [])
    .filter((item) => !HIDDEN_OVERVIEW_READINESS_KEYS.has(item.key));
  const incomplete = (readiness.incomplete_items || [])
    .filter((item) => !HIDDEN_OVERVIEW_READINESS_KEYS.has(item.key));
  const scoredItems = visibleReadinessItems.filter((item) => item.progress_pct != null || item.status);
  const score = scoredItems.length
    ? Math.round(scoredItems.reduce((sum, item) => {
      if (item.progress_pct != null) return sum + clampPct(item.progress_pct);
      return sum + (item.status === "ok" ? 100 : 0);
    }, 0) / scoredItems.length)
    : readiness.readiness_score || 0;
  const actionTabFor = (item) => {
    if (item.key === "transportation" || item.key === "accommodation") return "vendors";
    return item.action?.tab;
  };
  const progressDisplayFor = (item) => {
    if (item.progress_pct == null) {
      return { pct: null, label: "Restricted", detail: item.detail };
    }
    return {
      pct: clampPct(item.progress_pct),
      label: item.progress_pct === 100 ? "Ready" : `${item.progress_pct}%`,
      detail: item.detail,
    };
  };

  return (
    <div data-testid="trip-readiness-panel" className="relative overflow-hidden rounded-lg border border-[#E2DFD7] bg-white p-6 shadow-sm">
      <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#2D7A65] via-[#7AAB97] to-[#E5B85C]" aria-hidden="true" />
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[#516259]">Trip readiness</p>
          <h3 className="mt-1 text-2xl font-bold text-[#1A2420]" data-testid="trip-readiness-score">{score}%</h3>
          <p className="text-sm text-slate-500">{readiness.ready ? "This trip is ready to run." : "Review the incomplete items before running this trip."}</p>
        </div>
        <div className="h-2 w-44 overflow-hidden rounded-full bg-[#F0EEE8]">
          <div className="h-full rounded-full bg-[#2D7A65] transition-[width] duration-500" style={{ width: `${score}%` }} />
        </div>
      </div>

      <div className="divide-y divide-[#ECEAE4]">
        {visibleReadinessItems.map((item) => {
          const Icon = STATUS_ICON[item.status] || AlertTriangle;
          const progress = progressDisplayFor(item);
          const actionTab = actionTabFor(item);
          const actionLabel = item.status === "ok" ? "Edit" : item.action?.label;
          return (
            <div key={item.key} data-testid={`readiness-item-${item.key}`} className="-mx-2 grid gap-3 rounded-md px-2 py-2.5 transition-colors hover:bg-[#FBF9F4] sm:grid-cols-[minmax(0,1fr)_minmax(13rem,auto)] sm:items-center">
              <div className="flex min-w-0 items-center gap-2">
                <Icon className={`h-4 w-4 shrink-0 ${STATUS_CLASS[item.status] || "text-slate-500"}`} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1A2420]">{item.label}</p>
                  {progress.detail && <p className="text-xs text-slate-500">{progress.detail}</p>}
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                <span className={`text-xs font-semibold ${STATUS_CLASS[item.status] || "text-slate-500"}`}>{progress.label}</span>
                {item.action && (
                  <button
                    type="button"
                    disabled={!item.action.allowed || !actionTab}
                    onClick={() => item.action.allowed && actionTab && onGoTab(actionTab)}
                    className="rounded-md border border-[#E2DFD7] px-2 py-1 text-xs font-medium text-[#2D7A65] hover:bg-slate-50 disabled:text-slate-400"
                  >
                    {item.action.allowed ? actionLabel : "No permission"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {incomplete.length === 0 && (
        <div className="mt-4 border-t border-[#ECEAE4] pt-4">
          <p className="text-sm font-medium text-emerald-700">All readiness checks are complete.</p>
        </div>
      )}
    </div>
  );
}

export default function OverviewTab({ trip, onGoTab }) {
  const s = trip.stats || {};
  const batches = Array.isArray(trip.batches) ? trip.batches : [];
  const hasMultipleBatches = batches.length > 1;
  return (
    <div className="space-y-6">
      <ReadinessPanel trip={trip} onGoTab={onGoTab} />
      {trip.organizer_review_notes?.length > 0 && (
        <section className="space-y-3 border-b border-[#E2DFD7] pb-4">
          <h3 className="text-base font-semibold">Organizer review notes</h3>
          {trip.organizer_review_notes.map((note, index) => (
            <div key={index} className="text-sm">
              <p className="font-semibold">{note.title}</p>
              {note.description && <p className="mt-1 text-slate-600">{note.description}</p>}
            </div>
          ))}
        </section>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <KPI icon={Calendar} label="Itinerary days" value={`${s.days || 0}/${trip.duration_days}`} onClick={() => onGoTab("itinerary")} testid="kpi-days" />
        <KPI icon={Map} label="Guides" value={s.guides || 0} onClick={() => onGoTab("configuration")} testid="kpi-guides" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className={`rounded-lg border border-[#E2DFD7] bg-white p-6 shadow-sm transition-shadow hover:shadow-md ${hasMultipleBatches ? "lg:col-span-3" : "lg:col-span-2"}`}>
          <div className="mb-2 flex items-center justify-between gap-3">
            <h3 className="flex items-center gap-2 text-base font-semibold"><span className="h-2 w-2 rounded-full bg-[#E5B85C]" aria-hidden="true" />Summary</h3>
            <button type="button" onClick={() => onGoTab("configuration")} className="inline-flex min-h-8 items-center gap-1 rounded-md border border-[#E2DFD7] px-2 py-1 text-xs font-medium text-[#2D7A65] hover:bg-slate-50">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          </div>
          <p className="text-sm leading-relaxed text-slate-600">{trip.summary || trip.description || "No summary added yet. Use the Configuration tab to describe this trip."}</p>
        </div>
        <div className={`rounded-[1.5rem] border border-[#E5DED0] bg-[#FFFCF7] p-5 shadow-sm transition-shadow hover:shadow-md ${hasMultipleBatches ? "lg:col-span-3" : ""}`}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              {batches.length > 0 && <p className="text-[9px] font-black uppercase tracking-[0.16em] text-[#2D7A65]">Schedule & pricing</p>}
              <h3 className="mt-1 text-lg font-black text-[#17211D]">{batches.length ? "Departure batches" : "Key facts"}</h3>
            </div>
            <button type="button" onClick={() => onGoTab("configuration")} className="inline-flex min-h-8 items-center gap-1 rounded-md border border-[#E2DFD7] px-2 py-1 text-xs font-medium text-[#2D7A65] hover:bg-slate-50">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          </div>
          {batches.length ? (
            <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,16rem),1fr))]">
              {batches.map((batch, index) => <BatchFacts key={batch.id || `${batch.label}-${index}`} batch={batch} index={index} />)}
            </div>
          ) : (
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Base price</dt>
                <dd className="flex items-center gap-1.5 font-medium">{inr(trip.base_price_minor)} <GovBadge kind="system" label="Set" /></dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Capacity</dt><dd className="font-medium">{trip.capacity || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Start</dt><dd className="font-medium">{fmtDate(trip.start_date)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">End</dt><dd className="font-medium">{fmtDate(trip.end_date)}</dd></div>
            </dl>
          )}
        </div>
      </div>
    </div>
  );
}
